import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MFAService } from '@app/pages/login-page/services/verify-totp.service';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { TWO_FACTOR_DIALOG_OPTIONS } from '@app/utils/constants/two-factor.constants';
import { TwoFactorStatus } from '@app/utils/enums/two-factor.enum';
import { PasswordConfirmationModalComponent } from '../../modal-dialog/password-confirmation-modal/password-confirmation-modal.component';

@Component({
  selector: 'app-two-factor-toggle',
  standalone: true,
  imports: [CommonModule, MatSlideToggleModule, TranslateModule],
  templateUrl: './two-factor-toggle.component.html',
  styleUrl: './two-factor-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorToggleComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly twoFactorService = inject(MFAService);
  private readonly userStateService = inject(UserStateService);

  protected is2FAEnabled = signal<boolean>(false);
  protected loading = signal<boolean>(false);
  protected userEmail = computed(() => this.userStateService.userData()?.data.email ?? '');

  ngOnInit(): void {
    this.load2FAStatus();
  }

  protected onToggleChange(event: MatSlideToggleChange): void {
    const newState = event.checked;
    const previousState = this.is2FAEnabled();

    event.source.checked = previousState;

    this.loading.set(true);

    if (newState) {
      this.enable2FA();
    } else {
      this.disable2FA();
    }
  }

  private load2FAStatus(): void {
    this.loading.set(true);

    this.twoFactorService
      .get2FAStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (status) => {
          this.is2FAEnabled.set(status);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  private enable2FA(): void {
    const dialogRef = this.dialog.open(PasswordConfirmationModalComponent, {
      ...TWO_FACTOR_DIALOG_OPTIONS,
      data: { action: TwoFactorStatus.ENABLED },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loading.set(false);

      if (result?.completed) {
        this.twoFactorService.update2FAStatus(true);
        this.is2FAEnabled.set(true);
      }
    });
  }

  private disable2FA(): void {
    const dialogRef = this.dialog.open(PasswordConfirmationModalComponent, {
      ...TWO_FACTOR_DIALOG_OPTIONS,
      data: { action: TwoFactorStatus.DISABLED },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loading.set(false);

      if (result?.completed) {
        this.twoFactorService.update2FAStatus(false);
        this.is2FAEnabled.set(false);
      }
    });
  }
}
